import React, { useEffect, useRef } from "react";
import { Arrow } from "./Arrow";

export function Header({ portfolio, menuOpen, setMenuOpen, activeSection, onNavigate }) {
  const toggleRef = useRef(null);
  const drawerRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape" && menuOpen) {
        setMenuOpen(false);
        toggleRef.current?.focus();
        return;
      }

      if (event.key === "Tab" && menuOpen) {
        const drawer = drawerRef.current;
        if (!drawer) return;

        const focusable = [
          toggleRef.current,
          ...Array.from(drawer.querySelectorAll('button:not([disabled]), a[href]'))
        ].filter(Boolean);

        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.body.classList.toggle("menu-is-open", menuOpen);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.classList.remove("menu-is-open");
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [menuOpen, setMenuOpen]);

  const navigate = (id) => {
    setMenuOpen(false);
    onNavigate(id);
  };

  return (
    <>
      <a className="skip-link" href="#main-content">Skip to content</a>

      <button
        className="site-signature"
        type="button"
        onClick={() => navigate("home")}
        aria-label={`${portfolio.profile.name}, go to home`}
      >
        <span>{portfolio.profile.initials}</span>
        <small>{portfolio.profile.name}</small>
      </button>

      <button
        className="menu-toggle"
        type="button"
        ref={toggleRef}
        aria-label={menuOpen ? "Close navigation menu" : "Open navigation menu"}
        aria-expanded={menuOpen}
        aria-controls="navigation-drawer"
        onClick={() => setMenuOpen((current) => !current)}
      >
        <span /><span />
      </button>

      <button
        className={`menu-scrim ${menuOpen ? "is-open" : ""}`}
        type="button"
        aria-label="Close navigation menu"
        tabIndex={menuOpen ? 0 : -1}
        onClick={() => setMenuOpen(false)}
      />

      <aside
        ref={drawerRef}
        className={`menu-drawer ${menuOpen ? "is-open" : ""}`}
        id="navigation-drawer"
        aria-hidden={!menuOpen}
      >
        <div className="menu-drawer__header">
          <span>Index</span>
          <span>{portfolio.profile.initials} / 2026</span>
        </div>
        <nav className="menu-drawer__nav" aria-label="Primary navigation">
          {portfolio.navigation.map((item, index) => (
            <button
              type="button"
              key={item.id}
              className={activeSection === item.id ? "is-active" : ""}
              onClick={() => navigate(item.id)}
              tabIndex={menuOpen ? 0 : -1}
            >
              <small>{String(index + 1).padStart(2, "0")}</small>
              <span>{item.label}</span>
              <Arrow />
            </button>
          ))}
        </nav>
        <div className="menu-drawer__footer">
          <a href={`mailto:${portfolio.profile.email}`} tabIndex={menuOpen ? 0 : -1}>
            {portfolio.profile.email}
          </a>
          <span>{portfolio.profile.location}</span>
        </div>
      </aside>
    </>
  );
}
